import React, { createContext, useContext, useCallback, useState } from "react";
import { ToastContainer } from "../components/ToastContainer";
import { v4 as uuid } from "uuid";

interface IToastContextData {
  addToast(message: Omit<IToastMessages, "id">): void;
  removeToast(id: String): void;
}

export interface IToastMessages {
  id: string;
  type?: "success" | "info" | "error";
  description?: string;
  title: string;
}

const ToastContext = createContext<IToastContextData>({} as IToastContextData);

export const ToastProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [messages, setMessages] = useState<IToastMessages[]>([]);

  const addToast = useCallback(
    ({ title, type, description }: Omit<IToastMessages, "id">) => {
      const id = uuid();

      const toast = {
        id,
        title,
        type,
        description,
      };

      setMessages((state) => [...state, toast]);
    },
    []
  );

  const removeToast = useCallback((id: string) => {
    setMessages((state) => state.filter((message) => message.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      <>
        {children}
        <ToastContainer messages={messages} />
      </>
    </ToastContext.Provider>
  );
};

export function useToast(): IToastContextData {
  const toastContext = useContext(ToastContext);

  if (!toastContext) {
    throw new Error("useToast must be used within an ToastProvider");
  }

  return toastContext;
}
